import { Page } from "@components-common/layouts";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Loader } from "taltech-styleguide";
import { application } from "../../../../_runtime/bootstrap";
import { useLoading } from "../../../../components-common/_shared/loading/use-loading.hook";
import { FormProvider } from "../../../../components-common/forms/provider";
import { Toolbar } from "@components-common/toolbar";
import { useModal } from "@components-common/modals";
import { InputDescription, InputName } from "@components-common/forms";
import { ActionButton } from "../../../../components-common/buttons/ActionButton";
import { Formik, useFormikContext } from "formik";
import { getPresetCreationSchema } from "../schema";
import { RootieTable } from "../../../../components-common/tables/RootieTable";
import { deleteTableRow } from "../../../../components-common/_shared/actionButton.helper";

/**
 * Functional Component: MinMaxFormatter
 *
 * This component is used to format table cell content based on the row's type.
 * If the row type is "TEXT", it renders a placeholder "<>". Otherwise, it displays
 * the content of the specified cell property.
 *
 * @param {Object} row - The row object from the table data.
 * @param {string} cellProp - The property of the row object to be displayed.
 *
 * @returns {JSX.Element} A JSX element displaying either "<>" for "TEXT" type or
 * the value of the specified cell property.
 */
const MinMaxFormatter = (row: any, cellProp: string) => (
  <>{row.type && row.type === "TEXT" ? "<>" : row[cellProp]}</>
);

/**
 * Functional Component: PageContent
 *
 * This component renders the content of the page, including the toolbar, modal, and table for managing preset items.
 *
 * Props:
 * @param {Object} props - The properties passed to the component.
 * @param {string} props.id - The ID of the preset.
 * @param {Object} props.toolbar - The toolbar configuration object.
 * @param {Object} props.modalProps - The modal properties for adding new items.
 * @param {string} props.getSourceTable - The source URL for fetching table data.
 * @param {string} props.deleteSource - The source URL for deleting items from the table.
 *
 * Returns:
 * @returns {JSX.Element} A JSX element containing the page content.
 */
const PageContent = ({
  id,
  toolbar,
  modalProps,
  getSourceTable,
  deleteSource,
}: any) => {
  const { showModal } = useModal();
  const { pathname } = useLocation();
  const isSpaceFunctions = pathname.includes("space-functions");
  const tablePropName = isSpaceFunctions
    ? "spaceFunction"
    : "buildingParameter";
  const formik = useFormikContext();
  const newModalProps = {
    ...modalProps,
    props: {
      ...modalProps.props,
      id: id,
      getSource: modalProps.props.getSource + id,
    },
  };
  const buildingParametersColumns = [
    { id: "name", dataField: `${tablePropName}.name` },
    { id: "description", dataField: `${tablePropName}.description` },
    { id: "type", dataField: `${tablePropName}.type` },
    {
      id: "creationType",
      dataField: `${tablePropName}.creationType`,
      formatter: (cell: any, row: any) => (
        <>{row[tablePropName].creationType}</>
      ),
    },
    {
      id: "custom",
      text: "Min",
      dataField: `${tablePropName}.min`,
      formatter: (cell: any, row: any) =>
        MinMaxFormatter(row[tablePropName], "min"),
    },
    {
      id: "custom",
      text: "Max",
      dataField: `${tablePropName}.max`,
      formatter: (cell: any, row: any) =>
        MinMaxFormatter(row[tablePropName], "max"),
    },
    { id: "value", dataField: `${tablePropName}.value` },
  ];

  const tableProps: any = {
    cat: "table",
    type: "editor",
    id: "table_presets",
    props: {
      source: {
        type: "REMOTE",
        url: `${getSourceTable}`,
        key: `${getSourceTable}`,
        urlParams: {
          presetId: `{presetId}`,
        },
      },
      columns: isSpaceFunctions
        ? [
            { id: "name", dataField: `${tablePropName}.name` },
            { id: "description", dataField: `${tablePropName}.description` },
          ]
        : buildingParametersColumns,
      actions: [
        {
          id: "delete",
          onClick: (props: any) =>
            deleteTableRow({
              endpoint: `${deleteSource}`,
              props,
            }),
        },
      ],
    },
  };

  return (
    <>
      <Toolbar
        {...{
          ...toolbar,
          left: [{ kind: "save", onAction: () => formik.submitForm() }],
          right: ["back"],
        }}
      />
      <InputName elementClass="required" />
      <InputDescription />
      <ActionButton
        kind="open_modal"
        label={`Add new ${
          isSpaceFunctions ? "space functions" : "building parameters"
        }`}
        onAction={() => showModal(newModalProps)}
        className="mt-3"
      />
      <RootieTable {...tableProps} />
    </>
  );
};

type Props = {
  title: string;
  form: any;
  toolbar: any;
  getSourceTable: string;
  updateSourceForm: string;
};

/**
 * Functional Component: PresetEditPage
 *
 * This component renders the page for editing an existing preset. It includes a form, toolbar, and a table for managing preset items.
 *
 * Props:
 * @param {Object} props - The properties passed to the component.
 * @param {string} props.title - The title of the page.
 * @param {Object} props.form - The form configuration object.
 * @param {string} props.getSourceTable - The source URL for fetching table data.
 *
 * Returns:
 * @returns {JSX.Element} A JSX element containing the preset edit page.
 */
export const PresetEditPage = ({
  title,
  form,
  getSourceTable,
  ...rest
}: any) => {
  const nav = useNavigate();
  const [loading, setLoading] = useLoading(true);
  const [formInfo, setFormInfo] = useState<any>({});
  const { presetId } = useParams();

  const submitForm = (values: any) => {
    application
      .getHttpClient()
      .put(`/buildingms/presets/${presetId}`, values)
      .then(() => {
        nav(-1);
      });
  };

  useEffect(() => {
    application
      .getHttpClient()
      .get(`/buildingms/presets/${presetId}`)
      .then((res: any) => {
        const formProps = {
          ...form,
          element: res.body,
          empty: res.body,
          schema: getPresetCreationSchema(),
          onSubmit: submitForm,
        };
        setFormInfo(formProps);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  return (
    <Page title={title}>
      {loading && <Loader overlay />}
      {!loading && (
        <FormProvider>
          <Formik
            initialValues={formInfo.element}
            validationSchema={formInfo.schema}
            onSubmit={formInfo.onSubmit}
          >
            <PageContent
              id={presetId}
              form={form}
              getSourceTable={getSourceTable}
              {...rest}
            />
          </Formik>
        </FormProvider>
      )}
    </Page>
  );
};
